import React from "react";
import BenefitsofHydroponic from "../image/BenefitsofHydroponic.png";
import NFT from "../image/NFT.png";
import CommercialGreenhouse from "../image/CommercialGreenhouse.png";
import SustainableAgriculture from "../image/SustainableAgriculture.png";
import Monitoringsystems from "../image/monitoringsystems.png";
import DWCSystem from "../image/LettuceRaftHydroponics.png";
import AiryDWCHydroponic from "../image/AiryDWCHydroponic.png";
import {
  Leaf,
  Droplets,
  BarChart3,
  Globe2,
  Sprout,
  Star,
  Settings,
  ShieldCheck,
} from "lucide-react";


const solutions = [
  {
    title: "NFT System",
    text: "Ideal for leafy greens",
    image: NFT,
  },
  {
    title: "DWC System",
    text: "Simple and efficient",
    image: DWCSystem,
  },
  {
    title: "Vertical Farming",
    text: "More crops, less space",
    image:
      "https://images.unsplash.com/photo-1530836369250-ef72a3f5cda8?auto=format&fit=crop&w=900&q=85",
  },
  {
    title: "Greenhouse Solutions",
    text: "Controlled environment",
    image: CommercialGreenhouse,
  },
];

const products = [
  [
    "Lettuce",
    "Crisp & healthy",
    "https://images.unsplash.com/photo-1556801712-76c8eb07bbc9?w=800&q=80",
  ],
  [
    "Basil",
    "Aromatic & fresh",
    "https://images.unsplash.com/photo-1618375569909-3c8616cf7733?w=800&q=80",
  ],
  [
    "Spinach",
    "Nutritious & green",
    "https://images.unsplash.com/photo-1576045057995-568f588f82fb?w=800&q=80",
  ],
  [
    "Strawberry",
    "Sweet & juicy",
    "https://images.unsplash.com/photo-1464965911861-746a04b4bca6?w=800&q=80",
  ],
  [
    "Tomato",
    "Farm fresh",
    "https://images.unsplash.com/photo-1546094096-0df4bcaaa337?w=800&q=80",
  ],
  [
    "Cucumber",
    "Crisp & natural",
    "https://images.unsplash.com/photo-1449300079323-02e209d9d3a6?w=800&q=80",
  ],
];
const blogPosts = [
  {
    title: "5 Benefits of Hydroponic Farming",
    date: "Sep 20, 2026",
    image:
      "https://images.unsplash.com/photo-1464226184884-fa280b87c399?auto=format&fit=crop&w=1000&q=85",
  },
  {
    title: "How to Start a Small Hydroponic Farm",
    date: "Sep 12, 2026",
    image:
      "https://images.unsplash.com/photo-1492496913980-501348b61469?auto=format&fit=crop&w=1000&q=85",
  },
  {
    title: "The Future of Sustainable Agriculture",
    date: "Sep 05, 2026",
    image: SustainableAgriculture,
  },
];

function SectionHeading({ eyebrow, title, description, linkText, linkHref }) {
  return (
    <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
      <div className="max-w-3xl">
        <p className="mb-2 text-[11px] font-extrabold uppercase tracking-[0.22em] text-emerald-700">
          {eyebrow}
        </p>
        <h2 className="text-3xl font-black leading-tight text-slate-900 md:text-[38px]">
          {title}
        </h2>
        {description && (
          <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-600 md:text-base">
            {description}
          </p>
        )}
      </div>

      {linkText && (
        <a
          href={linkHref}
          className="inline-flex w-fit items-center rounded-full border border-emerald-600 px-4 py-2 text-xs font-extrabold text-emerald-700 transition hover:bg-emerald-700 hover:text-white"
        >
          {linkText} <span className="ml-2">→</span>
        </a>
      )}
    </div>
  );
}

function App() {
  const [menuOpen, setMenuOpen] = React.useState(false);

  return (
    <div className="min-h-screen overflow-hidden bg-white text-slate-900">
      {/* Hero */}
      <section
        id="home"
        className="relative isolate min-h-[600px] overflow-hidden"
      >
        <img
          src={AiryDWCHydroponic}
          alt="Hydroponic greenhouse"
          className="absolute inset-0 -z-20 h-full w-full object-cover"
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-emerald-950 via-emerald-950/85 to-emerald-950/10" />

        <div className="mx-auto flex min-h-[600px] max-w-[1440px] items-center px-5 py-20 lg:px-10">
          <div className="max-w-2xl text-white">
            <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-emerald-200 md:text-sm">
              Smart Agriculture • Sustainable Future
            </p>
            <h1 className="mt-5 text-5xl font-black leading-[0.98] md:text-7xl">
              Fresh Food
              <br />
              <span className="text-emerald-300">Grown Smarter</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-7 text-emerald-50 md:text-lg">
              Innovative hydroponic farming solutions for a healthier planet,
              better food and a brighter tomorrow.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="/solutions"
                className="rounded-full bg-emerald-500 px-6 py-3.5 text-sm font-extrabold text-white transition hover:bg-emerald-400"
              >
                Explore Our Solutions →
              </a>
              <a
                href="#technology"
                className="rounded-full bg-white px-6 py-3.5 text-sm font-extrabold text-emerald-900 transition hover:bg-emerald-100"
              >
                ▶ &nbsp; Watch Video
              </a>
            </div>

            <div className="mt-10 flex flex-wrap gap-x-7 gap-y-4 text-xs font-bold md:text-sm">
              <span className="flex items-center gap-2">
                <Sprout
                  size={18}
                  strokeWidth={2}
                  className="text-emerald-600"
                />
                Soil Free
              </span>

              <span className="flex items-center gap-2">
                <Droplets size={18} strokeWidth={2} className="text-blue-500" />
                Water Efficient
              </span>

              <span className="flex items-center gap-2">
                <Leaf size={18} strokeWidth={2} className="text-emerald-600" />
                Higher Yield
              </span>

              <span className="flex items-center gap-2">
                <Globe2
                  size={18}
                  strokeWidth={2}
                  className="text-emerald-600"
                />
                Sustainable
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="border-b border-emerald-100 bg-emerald-50">
        <div className="mx-auto grid max-w-[1440px] gap-5 px-5 py-7 sm:grid-cols-2 lg:grid-cols-4 lg:px-10">
          {[
            {
              icon: Leaf,
              title: "100% Fresh & Safe",
              text: "Clean, controlled produce",
            },
            {
              icon: Droplets,
              title: "Uses Less Water",
              text: "Conserve resources",
            },
            {
              icon: BarChart3,
              title: "More Yield per Space",
              text: "Grow more, with less",
            },
            {
              icon: Globe2,
              title: "A Greener Planet",
              text: "Sustainable for future",
            },
          ].map(({ icon: Icon, title, text }) => (
            <div
              key={title}
              className="group flex items-center gap-4 border-slate-200 lg:border-r lg:last:border-r-0"
            >
              {/* Icon */}
              <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-white text-emerald-600 shadow-sm ring-1 ring-emerald-100 transition duration-300 group-hover:scale-110">
                <Icon size={24} strokeWidth={2} />
              </div>

              {/* Content */}
              <div>
                <h3 className="text-sm font-black text-slate-900">{title}</h3>

                <p className="mt-1 text-xs text-slate-600">{text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* About */}
      <section
        id="about"
        className="mx-auto grid max-w-[1440px] gap-12 px-5 py-20 lg:grid-cols-2 lg:items-center lg:px-10"
      >
        <div>
          <p className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-emerald-700">
            About Polybionic Technologies
          </p>
          <h2 className="mt-3 text-4xl font-black leading-tight md:text-5xl">
            Growing a Healthier
            <br />
            Future with Innovation
          </h2>
          <p className="mt-5 max-w-xl text-sm leading-7 text-slate-600 md:text-base">
            We design and build modern hydroponic farming systems to support
            fresh, nutritious food production. Our mission is to combine
            innovation, sustainability and practical agriculture solutions.
          </p>
          <a
            href="/about-us"
            className="mt-7 inline-flex rounded-full bg-emerald-700 px-6 py-3 text-sm font-extrabold text-white hover:bg-emerald-800"
          >
            Learn More About Us →
          </a>
        </div>

        <div className="grid gap-5 md:grid-cols-[1fr_1fr] md:items-center">
          <div className="overflow-hidden rounded-[45%] border-[10px] border-emerald-100">
            <img
              src={BenefitsofHydroponic}
              alt="Fresh hydroponic lettuce"
              className="h-[330px] w-full object-cover"
              loading="lazy"
            />
          </div>
          <div className="grid gap-4">
            {[
              [
                "◉",
                "Our Vision",
                "A world with safe, sustainable and fresh food.",
              ],
              [
                "◉",
                "Our Mission",
                "To make modern farming simple and accessible.",
              ],
              [
                "✳",
                "Our Values",
                "Innovation, transparency and care for the planet.",
              ],
            ].map(([icon, title, text]) => (
              <div key={title} className="flex gap-3">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-emerald-100 font-bold text-emerald-700">
                  {icon}
                </span>
                <div>
                  <h3 className="text-sm font-black">{title}</h3>
                  <p className="mt-1 text-xs leading-5 text-slate-600">
                    {text}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact strip */}
      <section className="bg-emerald-950 text-white">
        <div className="mx-auto grid max-w-[1440px] gap-7 px-5 py-8 sm:grid-cols-2 lg:grid-cols-4 lg:px-10">
          {[
            ["♧", "Growing", "With Communities"],
            ["♧", "Innovative", "Farming Solutions"],
            ["◒", "Real Impact", "In Agriculture"],
            ["◎", "Building", "A Sustainable Future"],
          ].map(([icon, title, text]) => (
            <div
              key={title}
              className="flex items-center justify-center gap-3 lg:justify-start"
            >
              <span className="text-3xl text-emerald-300">{icon}</span>
              <div>
                <p className="font-black">{title}</p>
                <p className="text-sm text-emerald-100">{text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Solutions */}
      <section
        id="solutions"
        className="mx-auto max-w-[1440px] px-5 py-20 lg:px-10"
      >
        <SectionHeading
          eyebrow="Our Solutions"
          title="Modern Farming Solutions for Every Need"
          linkText="View All Solutions"
          linkHref="/solutions"
        />

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {solutions.map((solution) => (
            <article
              key={solution.title}
              className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl"
            >
              <img
                src={solution.image}
                alt={solution.title}
                className="h-44 w-full object-cover"
                loading="lazy"
              />
              <div className="p-4">
                <h3 className="text-sm font-black">{solution.title}</h3>
                <p className="mt-1 text-xs text-slate-500">{solution.text}</p>
                <a
                  href=""
                  className="mt-4 inline-block text-xs font-bold text-emerald-700"
                >
                  Learn More →
                </a>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Products */}
      <section id="products" className="bg-slate-50">
        <div className="mx-auto max-w-[1440px] px-5 py-20 lg:px-10">
          <SectionHeading
            eyebrow="Our Products"
            title="Fresh, Nutritious and Locally Grown"
            linkText="View All Products"
            linkHref="#contact"
          />

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {products.map(([title, text, image]) => (
              <article
                key={title}
                className="group rounded-xl border border-slate-200 bg-white p-3 transition duration-300 hover:-translate-y-1 hover:shadow-lg"
              >
                {/* Product Image */}
                <div className="h-28 overflow-hidden rounded-lg bg-emerald-50">
                  <img
                    src={image}
                    alt={title}
                    className="h-full w-full object-cover transition duration-500 group-hover:scale-110"
                    loading="lazy"
                  />
                </div>

                {/* Product Details */}
                <h3 className="mt-3 text-xs font-black md:text-sm">{title}</h3>

                <p className="mt-1 text-[10px] text-slate-500 md:text-xs">
                  {text}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>
      {/* Technology */}
      <section
        id="technology"
        className="mx-auto grid max-w-[1440px] gap-12 px-5 py-20 lg:grid-cols-2 lg:items-center lg:px-10"
      >
        <div className="overflow-hidden rounded-[30px]">
          <img
            src={Monitoringsystems}
            alt="Smart farming monitoring equipment"
            className="h-[420px] w-full object-cover"
            loading="lazy"
          />
        </div>

        <div>
          <p className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-emerald-700">
            Smart Farming Technology
          </p>
          <h2 className="mt-3 text-3xl font-black leading-tight md:text-4xl">
            Data-Driven Farming for Better Results
          </h2>
          <p className="mt-5 text-sm leading-7 text-slate-600 md:text-base">
            Smart monitoring systems can help farmers understand and optimize
            their growing environment in real time.
          </p>
          <ul className="mt-6 space-y-3 text-sm text-slate-700">
            {[
              "Real-time pH and EC monitoring",
              "Temperature and humidity control",
              "Automated nutrient management",
              "Remote access through web and mobile apps",
            ].map((item) => (
              <li key={item} className="flex gap-2">
                <span className="font-black text-emerald-700">✓</span>
                {item}
              </li>
            ))}
          </ul>
          <a
            href="/technology"
            className="mt-7 inline-flex rounded-full bg-emerald-700 px-6 py-3 text-sm font-extrabold text-white hover:bg-emerald-800"
          >
            Explore Technology →
          </a>
        </div>
      </section>

      {/* Why choose us */}
      <section className="border-y border-emerald-100 bg-emerald-50">
        <div className="mx-auto max-w-[1440px] px-5 py-10 lg:px-10">
          <SectionHeading
            eyebrow="Why Choose Us"
            title="A Better Way to Grow"
          />

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                icon: Leaf,
                title: "Sustainable",
                text: "Eco-friendly farming",
              },
              {
                icon: Star,
                title: "High Quality",
                text: "Fresh and nutritious",
              },
              {
                icon: Settings,
                title: "Expert Support",
                text: "From setup to harvest",
              },
              {
                icon: ShieldCheck,
                title: "Reliable Systems",
                text: "Built for long-term use",
              },
            ].map(({ icon: Icon, title, text }) => (
              <div
                key={title}
                className="flex items-start gap-3 rounded-xl bg-white p-5"
              >
                {/* Icon */}
                <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-emerald-50 text-emerald-700">
                  <Icon size={26} strokeWidth={2} />
                </div>

                {/* Content */}
                <div>
                  <h3 className="text-sm font-black">{title}</h3>

                  <p className="mt-1 text-xs text-slate-600">{text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="mx-auto max-w-[1440px] px-5 py-20 lg:px-10">
        <SectionHeading
          eyebrow="Testimonials"
          title="What Our Clients Say"
          description="Replace these sample reviews with genuine customer feedback and permission-based testimonials."
        />
        <div className="grid gap-5 md:grid-cols-3">
          {[
            [
              "Rahul Mehta",
              "Farm Owner, Delhi",
              "Polybionic Technologies helped us understand modern farming systems.",
            ],
            [
              "Priya Sharma",
              "Restaurant Owner, Mumbai",
              "Professional team, clear communication and useful guidance.",
            ],
            [
              "Amit Verma",
              "Business Partner, Bangalore",
              "A practical approach to hydroponic solutions and agriculture.",
            ],
          ].map(([name, role, quote]) => (
            <blockquote key={name} className="rounded-2xl bg-slate-50 p-6">
              <p className="text-sm italic leading-7 text-slate-600">
                “{quote}”
              </p>
              <div className="mt-5">
                <p className="text-sm font-black">{name}</p>
                <p className="mt-1 text-xs text-slate-500">{role}</p>
              </div>
            </blockquote>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-emerald-950 text-white">
        <div className="mx-auto flex max-w-[1440px] flex-col justify-between gap-6 px-5 py-12 md:flex-row md:items-center lg:px-10">
          <div>
            <h2 className="text-3xl font-black md:text-4xl">
              Let's Grow a Greener Tomorrow
            </h2>
            <p className="mt-2 text-sm text-emerald-100">
              Get in touch with our team for a free consultation.
            </p>
          </div>
          <a
            href="#contact"
            className="w-fit rounded-full bg-white px-6 py-3.5 text-sm font-black text-emerald-900 hover:bg-emerald-100"
          >
            Contact Us Today →
          </a>
        </div>
      </section>

      {/* Blog */}
      <section id="blog" className="mx-auto max-w-[1440px] px-5 py-20 lg:px-10">
        <SectionHeading
          eyebrow="From Our Blog"
          title="Latest News & Insights"
          linkText="View All Articles"
          linkHref="#contact"
        />
        <div className="grid gap-5 md:grid-cols-3">
          {blogPosts.map((post) => (
            <article
              key={post.title}
              className="overflow-hidden rounded-xl border border-slate-200"
            >
              <img
                src={post.image}
                alt={post.title}
                className="h-52 w-full object-cover"
                loading="lazy"
              />
              <div className="p-5">
                <p className="text-xs text-slate-500">{post.date}</p>
                <h3 className="mt-3 text-base font-black">{post.title}</h3>
                <a
                  href="#contact"
                  className="mt-5 inline-block text-xs font-bold text-emerald-700"
                >
                  Read More →
                </a>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}

export default App;
